import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  X,
  AlertCircle,
  AlertTriangle,
  Info,
  GripVertical,
  Save,
  Loader2,
  Settings,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import type { DisputeType } from '@/types/dispute-types';
import { useCreateDisputeType } from '@/hooks/useDisputeTypesData';
import { useUsersList } from '@/hooks/useUsersData';
import { useTemplatesList } from '@/hooks/useTemplatesData';

interface WorkflowLevel {
  id: string;
  levelNumber: number;
  description: string;
  followUpDays: number;
  escalationDays: number;
  assignedUser: string;
  escalationUser: string;
  documentTemplate: string;
}

function getLevelColor(index: number): string {
  const colors = ['#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
  return colors[index] ?? '#8b5cf6';
}

function getLevelTooltip(levelNumber: number): string {
  const tooltips = [
    'Initial contact level - First attempt to resolve the dispute',
    'Follow-up level - Second attempt if unresolved after initial contact',
    'Escalation level - Escalated to higher authority or specialized team',
    'Senior escalation - Escalated to senior management or executive team',
    'Final resolution - Last resort before external measures',
  ];
  return tooltips[levelNumber - 1] || `Level ${levelNumber} escalation`;
}

const defaultLevel = (): WorkflowLevel => ({
  id: `${Date.now()}`,
  levelNumber: 1,
  description: '',
  followUpDays: 1,
  escalationDays: 2,
  assignedUser: 'Unassigned',
  escalationUser: 'Unassigned',
  documentTemplate: 'None',
});

const CreateDisputeTypePage: React.FC = () => {
  const navigate = useNavigate();
  const createDisputeTypeMutation = useCreateDisputeType();
  const { data: users = [] } = useUsersList();

  const activeUsers = users
    .filter(u => u.status === 'active')
    .map(u => ({ id: u.id, name: `${u.firstName} ${u.lastName}` }));

  const { data: templatesData } = useTemplatesList();
  const emailTemplates = templatesData?.filter(t => t.type === 'email') || [];

  const [formData, setFormData] = useState({ name: '', description: '' });
  const [levels, setLevels] = useState<WorkflowLevel[]>([defaultLevel()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const getValidationErrorsMap = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];

      if (!level.description.trim()) {
        newErrors[`level-${i}-description`] = `Level ${i + 1}: Description is required`;
      }
      if (level.escalationDays <= level.followUpDays) {
        newErrors[`level-${i}`] = `Level ${i + 1}: Escalation days must be greater than follow-up days`;
      }
      if (i > 0 && level.escalationDays <= levels[i - 1].escalationDays) {
        newErrors[`level-${i}-progression`] = `Level ${i + 1}: Escalation days must be greater than previous level`;
      }
      if (level.assignedUser === 'Unassigned') {
        newErrors[`level-${i}-user`] = `Level ${i + 1}: Assigned user is required`;
      }
    }

    return newErrors;
  };

  const isFormValid = () => Object.keys(getValidationErrorsMap()).length === 0;

  const getValidationErrors = (): string[] => {
    const map = getValidationErrorsMap();
    const messages: string[] = [];
    if (map.name) messages.push('Dispute type name is required');
    for (let i = 0; i < levels.length; i++) {
      if (map[`level-${i}`]) messages.push(map[`level-${i}`]);
      if (map[`level-${i}-progression`]) messages.push(map[`level-${i}-progression`]);
      if (map[`level-${i}-description`]) messages.push(map[`level-${i}-description`]);
      if (map[`level-${i}-user`]) messages.push(map[`level-${i}-user`]);
    }
    return messages.slice(0, 5);
  };

  const handleAddLevel = () => {
    const last = levels[levels.length - 1];
    setLevels([
      ...levels,
      {
        id: `${Date.now()}`,
        levelNumber: levels.length + 1,
        description: '',
        followUpDays: last ? last.escalationDays + 1 : 1,
        escalationDays: last ? last.escalationDays + 2 : 2,
        assignedUser: 'Unassigned',
        escalationUser: 'Unassigned',
        documentTemplate: 'None',
      },
    ]);
  };

  const handleDeleteLevel = (levelId: string) => {
    setLevels(
      levels
        .filter(l => l.id !== levelId)
        .map((level, index) => ({ ...level, levelNumber: index + 1 }))
    );
  };

  const handleLevelChange = (levelId: string, field: keyof WorkflowLevel, value: string | number) => {
    setLevels(levels.map(l => (l.id === levelId ? { ...l, [field]: value } : l)));
  };

  const moveLevel = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= levels.length) return;
    const updatedLevels = [...levels];
    const [movedLevel] = updatedLevels.splice(fromIndex, 1);
    updatedLevels.splice(toIndex, 0, movedLevel);
    setLevels(updatedLevels.map((level, i) => ({ ...level, levelNumber: i + 1 })));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    moveLevel(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = () => {
    const validationErrors = getValidationErrorsMap();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const stages = levels.map((level, index) => ({
      id: level.id || `stage-${Date.now()}-${index}`,
      name: `Level ${level.levelNumber}`,
      description: level.description || `Escalation level ${level.levelNumber}`,
      order: level.levelNumber,
      color: getLevelColor(index),
      properties: {
        slaHours: level.escalationDays * 24,
        slaWarningHours: level.followUpDays * 24,
        followUpDays: level.followUpDays,
        escalationDays: level.escalationDays,
        allowedAssigneeRoles: [level.assignedUser],
        escalationUser: level.escalationUser,
        documentTemplate: level.documentTemplate,
        requiresApproval: false,
        autoEscalate: true,
        allowReassignment: false,
      },
      notifications: [],
    }));

    stages.push({
      id: `stage-resolved-${Date.now()}`,
      name: 'Resolved',
      description: 'Dispute has been resolved',
      order: levels.length + 1,
      color: '#10b981',
      properties: {
        slaHours: 0,
        slaWarningHours: 0,
        followUpDays: 0,
        escalationDays: 0,
        allowedAssigneeRoles: [] as string[],
        escalationUser: '',
        documentTemplate: '',
        requiresApproval: false,
        autoEscalate: false,
        allowReassignment: false,
      },
      notifications: [],
    });

    const newDisputeType: DisputeType = {
      id: `dt-${Date.now()}`,
      ...formData,
      isActive: true,
      isSystemType: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'Current User',
      usageCount: 0,
      avgResolutionTime: 0,
      successRate: 0,
      workflow: {
        id: `wf-${Date.now()}`,
        disputeTypeId: `dt-${Date.now()}`,
        stages,
        transitions: [],
      },
    };

    createDisputeTypeMutation.mutate(newDisputeType, {
      onSuccess: () => {
        toast.success('Dispute type created successfully');
        navigate('/settings/dispute-types');
      },
      onError: (err) => {
        toast.error(`Failed to create dispute type: ${err.message}`);
      },
    });
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b bg-background px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
              <Settings className="h-4 w-4" />
              <span>Settings</span>
              <ChevronRight className="h-3.5 w-3.5" />
              <button
                type="button"
                className="hover:text-foreground cursor-pointer transition-colors bg-transparent border-none p-0 text-sm text-muted-foreground"
                onClick={() => navigate('/settings/dispute-types')}
              >
                Dispute Types
              </button>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-foreground font-medium">Create New Dispute Type</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Create New Dispute Type</h1>
            <p className="text-muted-foreground mt-1">
              Define the dispute type and configure its workflow levels with timelines and assignments
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/settings/dispute-types')}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={handleSave}
                      disabled={createDisputeTypeMutation.isPending}
                    >
                      {createDisputeTypeMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Create Dispute Type
                    </Button>
                  </span>
                </TooltipTrigger>
                {!isFormValid() && (
                  <TooltipContent className="max-w-sm">
                    <div className="space-y-1">
                      <p className="font-semibold">Please fix the following issues:</p>
                      <ul className="list-disc list-inside text-xs space-y-0.5">
                        {getValidationErrors().map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="container max-w-7xl mx-auto p-6 space-y-6">

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the name and description for this dispute type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Workflow Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      placeholder="e.g., Billing Error, Service Quality"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Disputes related to incorrect billing amounts, pricing discrepancies, or calculation errors"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                    />
                    <p className="text-sm text-muted-foreground">
                      Provide a clear description to help team members understand when to use this workflow.
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Workflow Configuration Tips
                      </p>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                        <li>Escalation days must be greater than follow-up days for each level</li>
                        <li>Escalation days must increase progressively across levels (e.g., 2 → 5 → 7 → 10)</li>
                        <li>All levels must have an assigned user</li>
                        <li>Drag and drop levels to reorder them</li>
                        <li>You can configure up to 5 escalation levels</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflow Levels */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Workflow Levels</CardTitle>
                  <CardDescription>
                    Define escalation levels with timelines and assignments
                  </CardDescription>
                </div>
                <Button onClick={handleAddLevel} size="sm" variant="outline" disabled={levels.length >= 5}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Level
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Setup Incomplete Warning Banner */}
              {levels.length > 0 && levels.some(l => l.assignedUser === 'Unassigned') && (
                <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                      Setup Incomplete: {levels.filter(l => l.assignedUser === 'Unassigned').length} of {levels.length} level(s) need configuration.
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                      Assign users to each workflow level for optimal automated dispute handling.
                    </p>
                  </div>
                </div>
              )}

              {/* Levels List */}
              <div className="space-y-3">
                {levels.map((level, index) => (
                  <Card
                    key={level.id}
                    className="border-l-4 border-l-primary hover:border-l-primary transition-colors"
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <CardContent className="pt-4">
                      <div className="space-y-4">
                        {/* Level Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-primary text-primary-foreground flex items-center gap-1">
                                    Level {level.levelNumber}
                                    <Info className="h-3 w-3" />
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{getLevelTooltip(level.levelNumber)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          {levels.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLevel(level.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                          <Label htmlFor={`desc-${level.id}`}>
                            Description <span className="text-destructive">*</span>
                          </Label>
                          <Textarea
                            id={`desc-${level.id}`}
                            placeholder="Enter level description (e.g., Initial contact level - First attempt to resolve the dispute)"
                            value={level.description}
                            onChange={(e) => handleLevelChange(level.id, 'description', e.target.value)}
                            rows={2}
                          />
                          {errors[`level-${index}-description`] && (
                            <p className="text-sm text-destructive flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {errors[`level-${index}-description`]}
                            </p>
                          )}
                        </div>

                        {/* Fields Grid */}
                        <div className="grid grid-cols-5 gap-4">
                          {/* Follow Up Days */}
                          <div className="space-y-2">
                            <Label htmlFor={`followUp-${level.id}`}>
                              Follow Up Days <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id={`followUp-${level.id}`}
                              type="number"
                              min="1"
                              value={level.followUpDays}
                              onChange={(e) =>
                                handleLevelChange(level.id, 'followUpDays', parseInt(e.target.value) || 1)
                              }
                            />
                          </div>

                          {/* Escalation Days */}
                          <div className="space-y-2">
                            <Label htmlFor={`escalation-${level.id}`}>
                              Escalation Days <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id={`escalation-${level.id}`}
                              type="number"
                              min="1"
                              value={level.escalationDays}
                              onChange={(e) =>
                                handleLevelChange(level.id, 'escalationDays', parseInt(e.target.value) || 1)
                              }
                            />
                          </div>

                          {/* Assigned User */}
                          <div className="space-y-2">
                            <Label htmlFor={`user-${level.id}`}>
                              Assigned User <span className="text-destructive">*</span>
                            </Label>
                            <Select
                              value={level.assignedUser}
                              onValueChange={(v) => handleLevelChange(level.id, 'assignedUser', v)}
                            >
                              <SelectTrigger id={`user-${level.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Unassigned">Unassigned</SelectItem>
                                {activeUsers.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Escalation User */}
                          <div className="space-y-2">
                            <Label htmlFor={`escUser-${level.id}`}>Escalation User</Label>
                            <Select
                              value={level.escalationUser}
                              onValueChange={(v) => handleLevelChange(level.id, 'escalationUser', v)}
                            >
                              <SelectTrigger id={`escUser-${level.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Unassigned">Unassigned</SelectItem>
                                {activeUsers.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Document Template */}
                          <div className="space-y-2">
                            <Label htmlFor={`template-${level.id}`}>
                              Document Template
                            </Label>
                            <Select
                              value={level.documentTemplate}
                              onValueChange={(v) => handleLevelChange(level.id, 'documentTemplate', v)}
                            >
                              <SelectTrigger id={`template-${level.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="None">None</SelectItem>
                                {emailTemplates.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Level-level errors */}
                        {(errors[`level-${index}`] || errors[`level-${index}-progression`] ||
                          errors[`level-${index}-user`]) && (
                          <div className="space-y-1">
                            {errors[`level-${index}`] && (
                              <p className="text-sm text-destructive flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {errors[`level-${index}`]}
                              </p>
                            )}
                            {errors[`level-${index}-progression`] && (
                              <p className="text-sm text-destructive flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {errors[`level-${index}-progression`]}
                              </p>
                            )}
                            {errors[`level-${index}-user`] && (
                              <p className="text-sm text-destructive flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {errors[`level-${index}-user`]}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default CreateDisputeTypePage;
